import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { kpisAPI, financeAPI, leadsAPI, tasksAPI, teamAPI, invoicesAPI, projectsAPI, clientsAPI, quipuAPI, serviceDeliveryAPI } from '../lib/api';
import { useAuthStore } from '../store';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Users,
  Briefcase,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Target,
  Calendar,
  ArrowUpRight,
  ArrowRight,
  ArrowDownRight,
  Activity,
  PieChart,
  BarChart3,
  Loader2,
  AlertTriangle,
  MessageSquare,
  Heart,
  Receipt,
  Megaphone,
  Clock,
  CheckSquare,
  UserCheck,
  Mail,
  AlertCircle,
  Wallet,
  CreditCard,
  Plane,
  Wrench,
  Building,
  Settings,
  Save,
  Plus,
  Trash2,
  RefreshCw,
  ClipboardList,
  Bell,
} from 'lucide-react';
import { toast } from 'sonner';
import { BankSyncAlert } from './BankSyncAlert';
import { BirthdayReminder } from './BirthdayReminder';
import { PendingApprovalsPanel } from './PendingApprovalsPanel';
import { StaleDataNote } from './StaleDataNote';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  ReferenceLine,
  ComposedChart,
  Legend,
} from 'recharts';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const COLORS = ['#10b981', '#f56258', '#3b82f6', '#f59e0b', '#8b5cf6'];

// Icon mapping for expense categories
const ICON_MAP = {
  wrench: Wrench,
  users: Users,
  plane: Plane,
  building: Building,
  receipt: Receipt,
  creditcard: CreditCard,
};

export function CEODashboard() {
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);
  const [kpis, setKpis] = useState(null);
  const [finance, setFinance] = useState(null);
  const [dashboardFinance, setDashboardFinance] = useState(null);
  const [realDashboard, setRealDashboard] = useState(null);
  const [leads, setLeads] = useState([]);
  const [team, setTeam] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [crmClients, setCrmClients] = useState([]);
  const [quipuData, setQuipuData] = useState(null);
  const [serviceClients, setServiceClients] = useState([]);
  const [remindingId, setRemindingId] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Config modal state — only the fields the user actually edits
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastAutoRefresh, setLastAutoRefresh] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);  // null = current month default
  const [editConfig, setEditConfig] = useState({
    monthly_goal: 0,
    ads_investment: 0,
    agency_meta_ad_account_id: '',
  });
  const [metaAccounts, setMetaAccounts] = useState([]);
  const loadMetaAccounts = async () => {
    try {
      const d = await serviceDeliveryAPI.getMetaAdAccounts();
      setMetaAccounts((d?.accounts || []).filter((a) => a.active));
    } catch (e) { /* silencioso */ }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Reload only the real dashboard when month changes
  useEffect(() => {
    if (selectedMonth === null) return; // initial load handles it
    (async () => {
      try {
        const realData = await kpisAPI.getCEODashboardReal(selectedMonth);
        setRealDashboard(realData);
      } catch (e) {
        console.error('Error reloading dashboard for month:', e);
      }
    })();
  }, [selectedMonth]);

  // Pipeline real-time refresh: cada 30s + al recuperar foco de pestaña
  useEffect(() => {
    const PIPELINE_POLL_MS = 30 * 1000;
    let cancelled = false;

    const refreshPipeline = async () => {
      try {
        const [realData, tasksData] = await Promise.all([
          kpisAPI.getCEODashboardReal(selectedMonth),
          tasksAPI.getAll().catch(() => null),
        ]);
        if (!cancelled) {
          setRealDashboard(realData);
          if (Array.isArray(tasksData)) setTasks(tasksData);
          setLastAutoRefresh(new Date());
        }
      } catch (e) {
        // Silent
      }
    };

    const interval = setInterval(refreshPipeline, PIPELINE_POLL_MS);

    // Refresh inmediato al volver a la pestaña
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshPipeline();
    };
    const onFocus = () => refreshPipeline();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [selectedMonth]);

  // Auto-refresh silencioso cada 5 minutos
  useEffect(() => {
    const AUTO_REFRESH_MS = 5 * 60 * 1000;
    const id = setInterval(async () => {
      try {
        const realData = await kpisAPI.getCEODashboardReal(selectedMonth);
        setRealDashboard(realData);
        setLastAutoRefresh(new Date());
      } catch (e) {
        // Silent — no toast, no spinner
        console.debug('Auto-refresh skipped:', e);
      }
    }, AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [selectedMonth]);

  const loadData = async () => {
    try {
      const [kpisData, financeData, dashboardData, realData, leadsData, teamData, invoicesData, tasksData, projectsData, crmClientsData, serviceClientsData] = await Promise.all([
        kpisAPI.getCEO().catch(() => null),
        financeAPI.getSummary().catch(() => null),
        financeAPI.getDashboardSummary().catch(() => null),
        kpisAPI.getCEODashboardReal().catch(() => null),
        leadsAPI.getAll().catch(() => []),
        teamAPI.getAll().catch(() => []),
        invoicesAPI.getAll().catch(() => []),
        tasksAPI.getAll().catch(() => []),
        projectsAPI.getAll().catch(() => []),
        serviceDeliveryAPI.getClients().catch(() => []),
        serviceDeliveryAPI.getClients().catch(() => []),
      ]);
      setKpis(kpisData);
      setFinance(financeData);
      setDashboardFinance(dashboardData);
      setRealDashboard(realData);
      setLeads(leadsData);
      setTeam(teamData);
      setInvoices(invoicesData);
      setTasks(tasksData);
      setProjects(projectsData);
      setCrmClients(crmClientsData || []);
      setServiceClients(Array.isArray(serviceClientsData) ? serviceClientsData : []);
      // quipuData replaced by realData.last_sync
      
      // Set edit config from real config (no fake defaults)
      if (dashboardData?.config || realData?.config) {
        setEditConfig({
          monthly_goal: dashboardData?.config?.monthly_goal || 0,
          ads_investment: dashboardData?.config?.ads_investment || 0,
          agency_meta_ad_account_id: realData?.config?.agency_meta_ad_account_id || '',
        });
      }
    } catch (error) {
      console.error('Error loading CEO dashboard:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveConfig = async () => {
    setConfigLoading(true);
    try {
      await financeAPI.updateConfig(editConfig);
      await loadData();
      setShowConfigModal(false);
    } catch (error) {
      console.error('Error saving config:', error);
    } finally {
      setConfigLoading(false);
    }
  };

  const navigateToBriefings = () => navigate('/service-delivery');

  const handleRemindBriefing = async (clientId) => {
    setRemindingId(clientId);
    try {
      const res = await serviceDeliveryAPI.remindBriefing(clientId);
      const parts = [];
      if (res?.email_sent) parts.push('email');
      if (res?.discord_sent) parts.push('Discord');
      toast.success(parts.length ? `Recordatorio enviado por ${parts.join(' y ')}` : 'Recordatorio enviado', { duration: 6000 });
    } catch (e) {
      toast.error(e?.message || 'No se pudo enviar el recordatorio');
    } finally {
      setRemindingId(null);
    }
  };

  const handleRefreshAll = async () => {
    setRefreshing(true);
    const toastId = toast.loading('Sincronizando Quipu y Google Sheets... Esto puede tardar hasta 2 minutos.');
    try {
      const result = await kpisAPI.refreshAllDashboard();
      const parts = [];
      if (result.quipu) parts.push('Quipu ✓');
      if (result.prospects_sheet) parts.push('Sheet ✓');
      if (result.errors?.length) {
        toast.warning(`Sincronización parcial. ${parts.join(' · ')}`, { id: toastId, description: result.errors.join(' | ') });
      } else {
        toast.success(`Datos actualizados (${parts.join(' · ')})`, { id: toastId });
      }
      await loadData();
    } catch (e) {
      toast.error('Error al sincronizar. Reintenta en unos segundos.', { id: toastId });
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // ============ DATOS REALES (sin valores inventados) ============
  // Pipeline de Ventas — usar prospectos reales del CRM (DB + sheet via realDashboard)
  const pipelineBuckets = realDashboard?.pipeline?.buckets || {};
  const pipelineData = [
    { name: 'Por contactar', value: pipelineBuckets.contactar || 0, color: '#9ca3af', status: 'contactar' },
    { name: 'Contactados', value: pipelineBuckets.contactado || 0, color: '#3b82f6', status: 'contactado' },
    { name: 'En seguimiento', value: pipelineBuckets.en_seguimiento || 0, color: '#f59e0b', status: 'en_seguimiento' },
    { name: 'Presupuesto', value: pipelineBuckets.presupuesto_enviado || 0, color: '#8b5cf6', status: 'presupuesto_enviado' },
    { name: 'Es cliente', value: pipelineBuckets.es_cliente || 0, color: '#10b981', status: 'es_cliente' },
  ];
  const totalProspectsReal = realDashboard?.pipeline?.total_leads || 0;
  const pipelineTotal = realDashboard?.pipeline?.total_value || 0;
  const hasPipelineData = realDashboard?.pipeline?.has_real_data || false;

  // Facturas vencidas y pendientes — desde Quipu real
  const overdueInvoices = realDashboard?.invoices?.overdue
    ? { length: realDashboard.invoices.overdue.count, total: realDashboard.invoices.overdue.total }
    : { length: 0, total: 0 };
  const pendingInvoicesStats = realDashboard?.invoices?.pending
    ? { count: realDashboard.invoices.pending.count, total: realDashboard.invoices.pending.total }
    : { count: 0, total: 0 };
  const pendingInvoicesList = []; // Lista detallada solo si hay facturas locales (DB). Quipu solo da counts/totals.

  // Clientes activos — del CRM real
  const activeClients = crmClients.filter(c => c.status === 'active' || c.estado === 'activo');
  const activeProjectsCount = realDashboard?.counts?.active_projects ?? projects.filter(p => p.status === 'active').length;

  // Facturación del mes — Quipu real
  const thisMonthTotal = realDashboard?.billing?.this_month || 0;
  const lastMonthTotal = realDashboard?.billing?.last_month || 0;
  const billingChange = realDashboard?.billing?.change_pct ?? 0;

  // Meta mensual — configurada por CEO
  const monthlyGoal = realDashboard?.goal?.monthly_goal || 0;
  const goalConfigured = realDashboard?.goal?.configured || false;
  const goalProgress = realDashboard?.goal?.progress_pct || 0;

  // Marketing — datos reales de Meta Ads (vía Nango) o fallback manual
  const marketingData = realDashboard?.marketing || { ads_investment: 0, leads_from_ads: 0, cost_per_lead: 0, connected: false };
  const adsInvestment = marketingData.ads_investment || 0;
  const leadsFromAds = marketingData.leads_from_ads || 0;
  const costPerLead = marketingData.cost_per_lead || 0;
  const marketingConnected = marketingData.connected || false;
  const adsHistory = marketingData.history || [];
  const agencyCampaigns = marketingData.campaigns || [];
  const activeCampaigns = marketingData.active_campaigns || 0;
  const agencyAccountName = marketingData.account_name || '';
  const marketingNoAccount = !!marketingData.no_agency_account;

  // Saldos — desde Quipu/Bank real
  const realIncome = realDashboard?.saldos?.income || 0;
  const realExpenses = realDashboard?.saldos?.expenses || 0;
  const realBalance = realDashboard?.saldos?.balance || 0;

  // Desglose de gastos por categoría — desde transaction_tags reales
  const expenseCategoriesReal = (realDashboard?.expense_categories || []).map(cat => ({
    ...cat,
    icon: ICON_MAP[cat.icon] || Receipt,
  }));

  // Tareas reales (de DB) — misma fuente que "Mis Tareas"
  const pendingAll = tasks.filter(t => t.status !== 'completed');
  const teamMemberIds = new Set(team.filter(m => m.role !== 'ceo').map(m => m.id));
  const allTeamIds = new Set(team.map(m => m.id));
  const myTasks = pendingAll.filter(t => t.assigned_to === currentUser?.id);
  const teamTasks = pendingAll.filter(t => teamMemberIds.has(t.assigned_to));
  const clientTasks = pendingAll.filter(t => !!t.client_id && !allTeamIds.has(t.assigned_to));
  const upcomingTasks = [...myTasks].sort((a, b) => {
    const da = a.due_date ? new Date(a.due_date).getTime() : Infinity;
    const db = b.due_date ? new Date(b.due_date).getTime() : Infinity;
    return da - db;
  }).slice(0, 3);

  // Wellbeing — null hasta que haya datos reales
  const wellbeing = realDashboard?.wellbeing || null;


  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
      data-testid="ceo-dashboard"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard CEO</h1>
          <p className="text-muted-foreground mt-1">Vista ejecutiva de tu organización</p>
        </div>
        <div className="flex items-center gap-3">
          {lastAutoRefresh && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground" title="Auto-refresh cada 5 minutos">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span>Auto-sync: {lastAutoRefresh.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          )}
          {realDashboard?.last_sync && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              <span>Última actualización: {new Date(realDashboard.last_sync).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefreshAll}
            disabled={refreshing}
            data-testid="refresh-all-btn"
            title="Forzar sincronización de Quipu y Google Sheets"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Sincronizando...' : 'Sincronizar ahora'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setShowConfigModal(true); if (metaAccounts.length === 0) loadMetaAccounts(); }} data-testid="config-button">
            <Settings className="w-4 h-4 mr-2" />
            Configurar
          </Button>
          <Badge variant="outline" className="text-primary border-primary">
            <Activity className="w-3 h-3 mr-1" />
            En tiempo real
          </Badge>
        </div>
      </div>

      {/* Aviso de banco sin sincronizar (PSD2) */}
      <BankSyncAlert />

      {/* Recordatorio de cumpleaños del equipo */}
      <BirthdayReminder />

      {/* Publicaciones pendientes de aprobación del cliente */}
      <PendingApprovalsPanel />

      {/* Selector de mes */}
      {realDashboard?.available_months && realDashboard.available_months.length > 0 && (
        <motion.div variants={itemVariants} className="flex items-center gap-3" data-testid="month-selector">
          <div className="flex items-center gap-2 text-sm">
            <div className="p-2 rounded-lg bg-emerald-50 text-emerald-600">
              <Calendar className="w-4 h-4" />
            </div>
            <span className="font-medium text-muted-foreground">Periodo del dashboard:</span>
          </div>
          <Select
            value={selectedMonth || realDashboard.selected_month}
            onValueChange={(v) => setSelectedMonth(v)}
          >
            <SelectTrigger className="w-[240px] h-10 border-emerald-200 focus:ring-emerald-500 bg-white shadow-sm" data-testid="month-select-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-[400px]">
              {realDashboard.available_months.map((m) => {
                const isCurrent = m.value === `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
                const monthValue = realDashboard.billing.monthly_history?.find(h => h.month === m.value)?.value || 0;
                return (
                  <SelectItem
                    key={m.value}
                    value={m.value}
                    className="py-2.5 cursor-pointer"
                    data-testid={`month-option-${m.value}`}
                  >
                    <div className="flex items-center justify-between gap-4 w-full">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5 text-emerald-500" />
                        <span className="font-medium">{m.label}</span>
                        {isCurrent && (
                          <Badge variant="outline" className="text-[10px] h-4 px-1.5 border-emerald-300 text-emerald-700 bg-emerald-50">
                            actual
                          </Badge>
                        )}
                      </div>
                      <span className={`text-xs font-mono ${monthValue > 0 ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                        €{monthValue.toLocaleString('es-ES', { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          {selectedMonth && selectedMonth !== `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}` && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedMonth(`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`)}
              className="text-xs text-muted-foreground hover:text-emerald-600"
              data-testid="reset-to-current-btn"
            >
              ← Volver al mes actual
            </Button>
          )}
        </motion.div>
      )}

      <motion.div variants={itemVariants} className="flex items-center gap-2 pt-2" data-testid="section-finance">
        <Wallet className="w-4 h-4 text-primary" />
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Finanzas · bancos y facturación</span>
        <div className="h-px flex-1 bg-border" />
      </motion.div>

      {/* Bloque financiero: KPIs */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Facturación del mes */}
        <Card className="card-hover kpi-emerald" data-testid="kpi-billing">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  Facturación · {realDashboard?.available_months?.find(m => m.value === realDashboard?.selected_month)?.label || 'Mes actual'}
                </p>
                <p className="text-2xl font-bold mt-1">€{thisMonthTotal.toLocaleString()}</p>
                <div className="flex items-center gap-1 mt-2">
                  {parseFloat(billingChange) >= 0 ? (
                    <ArrowUpRight className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4 text-red-500" />
                  )}
                  <span className={`text-xs ${parseFloat(billingChange) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    {billingChange}% vs mes anterior
                  </span>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-white/80">
                <Receipt className="w-5 h-5 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Clientes Activos */}
        <Card className="card-hover kpi-blue" data-testid="kpi-clients">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Clientes Activos</p>
                <p className="text-2xl font-bold mt-1">{activeClients.length}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  {activeProjectsCount} con proyectos activos
                </p>
              </div>
              <div className="p-3 rounded-xl bg-white/80">
                <UserCheck className="w-5 h-5 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Meta Mensual */}
        <Card className="card-hover kpi-amber" data-testid="kpi-goal">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Meta Mensual</p>
                {goalConfigured ? (
                  <>
                    <p className="text-2xl font-bold mt-1">{goalProgress.toFixed(0)}%</p>
                    <Progress value={Math.min(goalProgress, 100)} className="h-2 mt-2" />
                    <p className="text-xs text-muted-foreground mt-1">
                      €{thisMonthTotal.toLocaleString()} / €{monthlyGoal.toLocaleString()}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-2xl font-bold mt-1 text-muted-foreground">€0</p>
                    <p className="text-xs text-muted-foreground mt-2">Sin meta configurada</p>
                    <Button variant="link" size="sm" className="px-0 h-auto text-xs text-amber-600" onClick={() => setShowConfigModal(true)}>
                      Configurar →
                    </Button>
                  </>
                )}
              </div>
              <div className="p-3 rounded-xl bg-white/80">
                <Target className="w-5 h-5 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Facturas Vencidas */}
        <Card className={`card-hover ${overdueInvoices.length > 0 ? 'kpi-coral border-red-200' : 'kpi-emerald'}`} data-testid="kpi-overdue">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Facturas Vencidas</p>
                <p className="text-2xl font-bold mt-1">{overdueInvoices.length}</p>
                {overdueInvoices.length > 0 && (
                  <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    €{overdueInvoices.total.toLocaleString()} pendiente
                  </p>
                )}
                {overdueInvoices.length === 0 && (
                  <p className="text-xs text-emerald-500 mt-2">Todo al día</p>
                )}
              </div>
              <div className="p-3 rounded-xl bg-white/80">
                <AlertCircle className={`w-5 h-5 ${overdueInvoices.length > 0 ? 'text-red-500' : 'text-emerald-500'}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Evolución de Facturación (últimos 12 meses) */}
      {realDashboard?.billing?.monthly_history && realDashboard.billing.monthly_history.some(m => m.value > 0) && (
        <motion.div variants={itemVariants}>
          <Card className="card-hover">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary" />
                    Evolución de Facturación
                  </CardTitle>
                  <CardDescription>Últimos 12 meses · datos reales de Quipu</CardDescription>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Total 12 meses</p>
                  <p className="text-lg font-bold text-emerald-600">
                    €{realDashboard.billing.monthly_history.reduce((s, m) => s + (m.value || 0), 0).toLocaleString('es-ES', { maximumFractionDigits: 0 })}
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={realDashboard.billing.monthly_history}
                    margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
                    onClick={(e) => {
                      if (e?.activePayload?.[0]?.payload?.month) {
                        setSelectedMonth(e.activePayload[0].payload.month);
                      }
                    }}
                  >
                    <defs>
                      <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.9} />
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0.4} />
                      </linearGradient>
                      <linearGradient id="barGradientSelected" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#059669" stopOpacity={1} />
                        <stop offset="100%" stopColor="#059669" stopOpacity={0.7} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                    <XAxis dataKey="label" fontSize={11} tick={{ fill: '#6b7280' }} />
                    <YAxis
                      fontSize={11}
                      tick={{ fill: '#6b7280' }}
                      tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                    />
                    <Tooltip
                      formatter={(v) => [`€${Number(v).toLocaleString('es-ES', { minimumFractionDigits: 2 })}`, 'Facturación']}
                      labelStyle={{ fontWeight: 600 }}
                      contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb' }}
                      cursor={{ fill: 'rgba(16, 185, 129, 0.05)' }}
                    />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]} style={{ cursor: 'pointer' }}>
                      {realDashboard.billing.monthly_history.map((m, idx) => (
                        <Cell
                          key={idx}
                          fill={m.is_selected ? 'url(#barGradientSelected)' : 'url(#barGradient)'}
                          stroke={m.is_selected ? '#047857' : 'transparent'}
                          strokeWidth={m.is_selected ? 2 : 0}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="text-xs text-muted-foreground text-center mt-2">
                💡 Haz click en cualquier barra para ver el detalle de ese mes
              </p>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Saldos + Facturas pendientes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Saldos: Ingresos / Gastos / Saldo */}
        <motion.div variants={itemVariants} className="lg:col-span-2">
          <Card className="card-hover">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wallet className="w-5 h-5 text-primary" />
                Saldos
              </CardTitle>
              <CardDescription>Ingresos, gastos y desglose por categoría</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-start justify-between mb-3">
                <p className="text-sm font-medium text-muted-foreground">Tesorería real del banco</p>
                {realDashboard?.saldos?.tx_count > 0 && (
                  <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-700 bg-blue-50">
                    {realDashboard.saldos.tx_count} transacciones
                  </Badge>
                )}
              </div>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                  <div className="flex items-center gap-2 mb-1">
                    <ArrowUpRight className="w-4 h-4 text-emerald-500" />
                    <span className="text-sm text-muted-foreground">Ingresos</span>
                  </div>
                  <p className="text-2xl font-bold text-emerald-600">
                    €{realIncome.toLocaleString()}
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-red-50 border border-red-100">
                  <div className="flex items-center gap-2 mb-1">
                    <ArrowDownRight className="w-4 h-4 text-red-500" />
                    <span className="text-sm text-muted-foreground">Gastos</span>
                  </div>
                  <p className="text-2xl font-bold text-red-500">
                    €{realExpenses.toLocaleString()}
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-blue-50 border border-blue-100">
                  <div className="flex items-center gap-2 mb-1">
                    <CreditCard className="w-4 h-4 text-blue-500" />
                    <span className="text-sm text-muted-foreground">Saldo</span>
                  </div>
                  <p className={`text-2xl font-bold ${realBalance >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                    €{realBalance.toLocaleString()}
                  </p>
                </div>
              </div>
              
              {/* Desglose por categoría — solo categorías REALES de transacciones bancarias */}
              <div className="space-y-3">
                <p className="text-sm font-medium text-muted-foreground">
                  Desglose de Gastos {realDashboard?.data_sources?.bank_transactions ? '(transacciones categorizadas)' : ''}
                </p>
                {expenseCategoriesReal.length === 0 ? (
                  <div className="text-center py-6 border-2 border-dashed rounded-lg text-sm text-muted-foreground">
                    Sin transacciones categorizadas aún.
                    <br />
                    <span className="text-xs">Ve a Finanzas → Conciliación para categorizar tus transacciones bancarias del mes.</span>
                  </div>
                ) : (
                  expenseCategoriesReal.map((cat, index) => {
                    const total = realExpenses || 1;
                    const pct = Math.round((cat.amount / total) * 100);
                    return (
                      <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${cat.color}`}>
                            <cat.icon className="w-4 h-4" />
                          </div>
                          <span className="font-medium">{cat.name}</span>
                          <Badge variant="outline" className="text-[10px]">{cat.count}</Badge>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold">€{cat.amount.toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground">{pct}%</p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Facturas pendientes detalle */}
        <motion.div variants={itemVariants} className="h-full">
          <Card className="card-hover h-full">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Receipt className="w-5 h-5 text-primary" />
                    Facturas Pendientes
                  </CardTitle>
                  <CardDescription>
                    {pendingInvoicesStats.count} facturas por cobrar • €{pendingInvoicesStats.total.toLocaleString()} total
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate('/finance-dashboard')} data-testid="pending-invoices-see-all">
                  Ver todas
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {pendingInvoicesStats.count === 0 ? (
                <div className="text-center py-6">
                  <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-2 opacity-50" />
                  <p className="text-muted-foreground">No hay facturas pendientes</p>
                </div>
              ) : pendingInvoicesList.length === 0 ? (
                <div className="text-center py-6">
                  <Receipt className="w-12 h-12 text-primary mx-auto mb-2 opacity-50" />
                  <p className="text-muted-foreground">Datos de Quipu cargados</p>
                  <p className="text-sm text-primary font-medium mt-2">{pendingInvoicesStats.count} facturas • €{pendingInvoicesStats.total.toLocaleString()}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingInvoicesList.slice(0, 5).map((invoice, index) => {
                    const isOverdue = new Date(invoice.due_date) < new Date();
                    return (
                      <div
                        key={index}
                        className={`flex items-center justify-between p-4 rounded-lg ${
                          isOverdue ? 'bg-red-50 border border-red-100' : 'bg-muted/30'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${isOverdue ? 'bg-red-100' : 'bg-white'}`}>
                            <Receipt className={`w-5 h-5 ${isOverdue ? 'text-red-500' : 'text-primary'}`} />
                          </div>
                          <div>
                            <p className="font-medium">{invoice.invoice_number}</p>
                            <p className="text-sm text-muted-foreground">{invoice.description}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">€{invoice.amount.toLocaleString()}</p>
                          <p className={`text-xs ${isOverdue ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}>
                            {isOverdue ? '⚠️ Vencida' : `Vence: ${new Date(invoice.due_date).toLocaleDateString('es-ES')}`}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <motion.div variants={itemVariants} className="flex items-center gap-2 pt-2" data-testid="section-operations">
        <Activity className="w-4 h-4 text-primary" />
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Operación · ventas, clientes y equipo</span>
        <div className="h-px flex-1 bg-border" />
      </motion.div>

      {/* Pipeline y Marketing */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pipeline de Ventas */}
        <motion.div variants={itemVariants}>
          <Card className="card-hover h-full">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    Pipeline de Ventas
                  </CardTitle>
                  <CardDescription>
                    {totalProspectsReal} prospectos totales{pipelineTotal > 0 ? ` • €${pipelineTotal.toLocaleString()} en pipeline` : ''}{realDashboard?.pipeline?.contracted_value > 0 ? ` • €${realDashboard.pipeline.contracted_value.toLocaleString('es-ES')} contratado (clientes activos)` : ''}
                  </CardDescription>
                </div>
                {hasPipelineData && (
                  <div className="flex items-center gap-1 text-[10px] text-emerald-600" title="Actualización automática cada 30s">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    <span>En vivo</span>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!hasPipelineData ? (
                <div className="text-center py-10 text-muted-foreground">
                  <TrendingUp className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Sin datos aún</p>
                  <p className="text-xs mt-1">Conecta el Google Sheet del CRM o añade prospectos manualmente.</p>
                </div>
              ) : (
                <div className="flex items-center gap-4">
                  <div className="w-32 h-32">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPie>
                        <Pie
                          data={pipelineData.filter(d => d.value > 0)}
                          cx="50%"
                          cy="50%"
                          innerRadius={30}
                          outerRadius={50}
                          paddingAngle={3}
                          dataKey="value"
                          onClick={(slice) => slice?.status && navigate(`/crm?estado=${slice.status}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          {pipelineData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v, n, p) => [`${v} prospectos`, p?.payload?.name]} />
                      </RechartsPie>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex-1 space-y-1">
                    {pipelineData.map((item, index) => (
                      <button
                        key={index}
                        onClick={() => navigate(`/crm?estado=${item.status}`)}
                        className="flex items-center justify-between w-full px-2 py-1.5 rounded-md hover:bg-emerald-50 hover:shadow-sm transition-all cursor-pointer group text-left"
                        data-testid={`pipeline-stage-${item.status}`}
                        title={`Ver prospectos en estado "${item.name}" en el CRM`}
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                          <span className="text-sm group-hover:text-emerald-700 group-hover:font-medium transition-all">{item.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="group-hover:bg-emerald-100 group-hover:text-emerald-700">{item.value}</Badge>
                          <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 text-emerald-600 transition-opacity" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Marketing: SOLO la cuenta de Meta Ads de Karma Digital (nunca datos de clientes) */}
        <motion.div variants={itemVariants}>
          <Card className="card-hover h-full" data-testid="marketing-widget">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Megaphone className="w-5 h-5 text-primary" />
                    Marketing · Karma Digital
                  </CardTitle>
                  <CardDescription data-testid="marketing-account-label">
                    {!marketingConnected
                      ? 'Meta Ads no conectado'
                      : marketingNoAccount
                        ? 'Cuenta propia de Meta Ads sin identificar'
                        : `Meta Ads · cuenta propia${agencyAccountName ? ` (${agencyAccountName})` : ''}`}
                  </CardDescription>
                </div>
                {marketingConnected ? (
                  <Badge variant="outline" className="text-xs border-emerald-300 text-emerald-700 bg-emerald-50" data-testid="marketing-connected-badge">
                    En vivo
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs text-muted-foreground">
                    Sin conexión
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-3"><StaleDataNote data={marketingData} testId="marketing-stale-note" /></div>
              {marketingData.unavailable && !marketingData.stale && (
                <p className="mb-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="marketing-unavailable">Meta no ha devuelto los datos ahora mismo. Vuelve a intentarlo en unos minutos.</p>
              )}
              <div className={`grid grid-cols-3 gap-4 mb-4 ${marketingData.unavailable && !marketingData.stale ? 'hidden' : ''}`}>
                <div className="text-center p-4 rounded-xl bg-blue-50" data-testid="marketing-spend">
                  <p className="text-2xl font-bold text-blue-600">€{adsInvestment.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  <p className="text-xs text-muted-foreground">Inversión Ads</p>
                </div>
                <div className="text-center p-4 rounded-xl bg-emerald-50" data-testid="marketing-leads">
                  <p className="text-2xl font-bold text-emerald-600">{leadsFromAds}</p>
                  <p className="text-xs text-muted-foreground">Leads</p>
                </div>
                <div className="text-center p-4 rounded-xl bg-amber-50" data-testid="marketing-cpl">
                  <p className="text-2xl font-bold text-amber-600">€{costPerLead.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  <p className="text-xs text-muted-foreground">Coste/Lead</p>
                </div>
              </div>

              {marketingConnected && marketingNoAccount && (
                <div className="text-center py-5 border-t mt-1" data-testid="marketing-no-account">
                  <p className="text-sm text-muted-foreground">No se ha encontrado la cuenta publicitaria de Karma Digital.</p>
                  <Button variant="link" size="sm" className="text-xs" onClick={() => setShowConfigModal(true)} data-testid="marketing-pick-account-btn">
                    Elegir cuenta propia en Configurar →
                  </Button>
                </div>
              )}

              {marketingConnected && !marketingNoAccount && adsInvestment === 0 && agencyCampaigns.length === 0 && (
                <div className="text-center py-5 border-t mt-1" data-testid="marketing-no-campaigns">
                  <Megaphone className="w-8 h-8 mx-auto mb-2 text-muted-foreground opacity-30" />
                  <p className="text-sm font-medium text-muted-foreground">Sin campañas activas este mes</p>
                  <p className="text-xs text-muted-foreground mt-1">Karma Digital no tiene inversión en Meta Ads en este periodo.</p>
                </div>
              )}

              {marketingConnected && agencyCampaigns.length > 0 && (
                <div className="border-t pt-3 mt-1 mb-3" data-testid="marketing-campaigns">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Campañas de Karma Digital · {activeCampaigns} con inversión</p>
                  <div className="space-y-1.5 max-h-44 overflow-y-auto">
                    {agencyCampaigns.map((c) => (
                      <div key={c.id} className="flex items-center justify-between text-xs" data-testid={`marketing-campaign-${c.id}`}>
                        <div className="flex-1 min-w-0 mr-2">
                          <p className="truncate font-medium text-foreground">{c.name}</p>
                          <p className="text-muted-foreground">{c.leads} leads · {(c.impressions || 0).toLocaleString('es-ES')} imp. · CPL €{(c.cost_per_lead || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                        </div>
                        <Badge variant="secondary" className="shrink-0">€{(c.spend || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {marketingConnected && !marketingNoAccount && adsHistory.some((h) => h.spend > 0 || h.leads > 0) && (
                <div className="border-t pt-3 mt-1" data-testid="marketing-history-chart">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Evolución últimos 6 meses · Inversión vs Leads (solo Karma Digital)</p>
                  <ResponsiveContainer width="100%" height={140}>
                    <ComposedChart data={adsHistory} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="#9ca3af" />
                      <YAxis yAxisId="left" tick={{ fontSize: 10 }} stroke="#3b82f6" tickFormatter={(v) => `€${Math.round(v)}`} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} stroke="#10b981" />
                      <Tooltip
                        contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                        formatter={(value, name) => {
                          if (name === 'Inversión') return [`€${Number(value).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, name];
                          return [value, name];
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
                      <Bar yAxisId="left" dataKey="spend" name="Inversión" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={18} />
                      <Line yAxisId="right" type="monotone" dataKey="leads" name="Leads" stroke="#10b981" strokeWidth={2} dot={{ r: 3, fill: '#10b981' }} activeDot={{ r: 5 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}

              {!marketingConnected && (
                <div className="text-center py-6 border-t mt-4">
                  <p className="text-xs text-muted-foreground">
                    Conecta tu cuenta de Meta Ads desde Integraciones para ver datos reales.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Briefings pendientes + Bienestar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Briefings pendientes */}
        {(() => {
          const pending = serviceClients.filter((c) => (c.briefing_status || 'pendiente') !== 'completado');
          const statusLabel = { pendiente: 'Pendiente', en_proceso: 'En proceso' };
          const statusColor = { pendiente: 'bg-gray-100 text-gray-700 border-gray-300', en_proceso: 'bg-amber-100 text-amber-700 border-amber-300' };
          return (
            <motion.div variants={itemVariants} className="lg:col-span-2">
              <Card className="card-hover" data-testid="pending-briefings-widget">
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="flex items-center gap-2">
                      <ClipboardList className="w-5 h-5 text-primary" />
                      Briefings pendientes
                      <Badge variant="outline" data-testid="pending-briefings-count">{pending.length}</Badge>
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={navigateToBriefings} data-testid="briefings-go-link">Ver en Entrega de Servicios</Button>
                  </div>
                  <CardDescription>Clientes cuyo briefing aún no está completado. Envíales un recordatorio en 1 clic.</CardDescription>
                </CardHeader>
                <CardContent>
                  {pending.length === 0 ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-4" data-testid="pending-briefings-empty">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500" /> Todos los briefings están completados.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {pending.map((c) => {
                        const st = c.briefing_status || 'pendiente';
                        const cid = c._id || c.id;
                        return (
                          <div key={cid} className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-muted/20" data-testid={`pending-briefing-row-${cid}`}>
                            <div className="min-w-0">
                              <p className="font-medium truncate">{c.nombre || c.name || 'Cliente'}</p>
                              <Badge variant="outline" className={`mt-1 ${statusColor[st] || ''}`}>{statusLabel[st] || st}</Badge>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={remindingId === cid}
                              onClick={() => handleRemindBriefing(cid)}
                              data-testid={`remind-briefing-btn-${cid}`}
                            >
                              {remindingId === cid ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Bell className="w-4 h-4 mr-1" />}
                              Recordar
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          );
        })()}

        {/* Bienestar del Cliente */}
        <motion.div variants={itemVariants}>
          <Card className="card-hover h-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-primary" />
                Bienestar del Cliente
              </CardTitle>
              <CardDescription>Clientes activos: aprobaciones, retrasos y pendientes</CardDescription>
            </CardHeader>
            <CardContent>
              {!wellbeing ? (
                <div className="text-center py-10 text-muted-foreground">
                  <Heart className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm font-medium">Sin datos aún</p>
                  <p className="text-xs mt-1">
                    Aparecerá cuando haya clientes activos en Entrega de Servicios.
                  </p>
                  {activeProjectsCount > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex items-center justify-between text-sm">
                        <span>Proyectos activos</span>
                        <Badge className="bg-emerald-100 text-emerald-700">{activeProjectsCount}</Badge>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-center p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-green-50">
                    <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-emerald-100 flex items-center justify-center">
                      <span className="text-2xl font-bold text-emerald-600" data-testid="wellbeing-satisfaction">{wellbeing.satisfaction_pct != null ? `${wellbeing.satisfaction_pct}%` : '—'}</span>
                    </div>
                    <p className="text-sm font-medium">Aprobado a la primera</p>
                    <p className="text-xs text-muted-foreground" data-testid="wellbeing-satisfaction-detail">
                      {wellbeing.satisfaction_pct != null ? `${wellbeing.approved} aprobadas · ${wellbeing.changes} con cambios (90 días)` : 'Sin revisiones del cliente en 90 días'}
                    </p>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Clientes al día</span>
                      <Badge className="bg-emerald-100 text-emerald-700" data-testid="wellbeing-on-time">{wellbeing.on_time || 0}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Con publicaciones atrasadas</span>
                      <Badge className="bg-amber-100 text-amber-700" data-testid="wellbeing-delayed">{wellbeing.delayed || 0}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Esperando aprobación del cliente</span>
                      <Badge className="bg-blue-100 text-blue-700" data-testid="wellbeing-pending">{wellbeing.pending_feedback || 0}</Badge>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Tareas Pendientes */}
      <div className="grid grid-cols-1 gap-6">
        {/* Tareas Pendientes */}
        <motion.div variants={itemVariants}>
          <Card className="card-hover h-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckSquare className="w-5 h-5 text-primary" />
                Tareas Pendientes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pendingAll.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Sin tareas pendientes</p>
                  <p className="text-xs mt-1">Las tareas creadas por el equipo aparecerán aquí.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {[
                    { key: 'mine', label: 'Mis tareas', count: myTasks.length, bg: 'bg-red-50 hover:bg-red-100', dot: 'bg-red-500', badge: 'bg-red-100 text-red-700' },
                    { key: 'team', label: 'Del equipo', count: teamTasks.length, bg: 'bg-amber-50 hover:bg-amber-100', dot: 'bg-amber-500', badge: 'bg-amber-100 text-amber-700' },
                    { key: 'clients', label: 'De clientes', count: clientTasks.length, bg: 'bg-blue-50 hover:bg-blue-100', dot: 'bg-blue-500', badge: 'bg-blue-100 text-blue-700' },
                  ].map((b) => (
                    <button
                      key={b.key}
                      type="button"
                      onClick={() => navigate(`/tasks?view=${b.key}`)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors group ${b.bg}`}
                      data-testid={`pending-tasks-${b.key}`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${b.dot}`} />
                        <span className="text-sm font-medium">{b.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={b.badge} data-testid={`pending-tasks-count-${b.key}`}>{b.count}</Badge>
                        <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </button>
                  ))}

                  <div className="pt-3 border-t">
                    <p className="text-xs text-muted-foreground mb-2">Próximas tareas:</p>
                    {upcomingTasks.length === 0 ? (
                      <p className="text-xs text-muted-foreground py-1">No tienes tareas pendientes asignadas.</p>
                    ) : upcomingTasks.map((task) => (
                      <button
                        key={task.id}
                        type="button"
                        onClick={() => navigate(`/tasks?view=mine&task=${task.id}`)}
                        className="w-full flex items-center gap-2 py-1.5 px-1 rounded hover:bg-muted/60 transition-colors text-left"
                        data-testid={`upcoming-task-${task.id}`}
                      >
                        <Clock className="w-3 h-3 text-muted-foreground shrink-0" />
                        <span className="text-sm truncate flex-1">{task.title}</span>
                        {task.due_date && (
                          <span className="text-xs text-muted-foreground shrink-0">{new Date(task.due_date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Modal de Configuración Financiera */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              Configuración del Dashboard
            </DialogTitle>
            <DialogDescription>
              Datos manuales del CEO. Los demás widgets toman datos reales de Quipu, banco y CRM automáticamente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="monthly_goal">Meta Mensual de Facturación (€)</Label>
              <Input
                id="monthly_goal"
                type="number"
                min="0"
                value={editConfig.monthly_goal}
                onChange={(e) => setEditConfig(prev => ({ ...prev, monthly_goal: parseFloat(e.target.value) || 0 }))}
                placeholder="Ej: 30000"
                data-testid="monthly-goal-input"
              />
              <p className="text-xs text-muted-foreground">
                La barra de progreso se calcula con la facturación real de Quipu del mes actual.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ads_investment">Inversión Manual en Ads (€)</Label>
              <Input
                id="ads_investment"
                type="number"
                min="0"
                value={editConfig.ads_investment}
                onChange={(e) => setEditConfig(prev => ({ ...prev, ads_investment: parseFloat(e.target.value) || 0 }))}
                placeholder="0"
                data-testid="ads-investment-input"
              />
              <p className="text-xs text-muted-foreground">
                Sólo mientras Meta Ads no esté conectado. Cuando conectes la cuenta, este valor se ignora.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Cuenta de Meta Ads de Karma Digital</Label>
              <Select
                value={editConfig.agency_meta_ad_account_id || 'auto'}
                onValueChange={(v) => setEditConfig(prev => ({ ...prev, agency_meta_ad_account_id: v === 'auto' ? '' : v }))}
                onOpenChange={(open) => { if (open && metaAccounts.length === 0) loadMetaAccounts(); }}
              >
                <SelectTrigger data-testid="agency-ad-account-select">
                  <SelectValue placeholder="Detectar automáticamente" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="auto">Detectar automáticamente (nombre con "Karma")</SelectItem>
                  {metaAccounts.map((a) => (
                    <SelectItem key={a.id} value={a.id} data-testid={`agency-ad-account-option-${a.id}`}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                El widget de Marketing del dashboard muestra solo esta cuenta (la de la agencia), nunca las de clientes.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveConfig} disabled={configLoading} data-testid="save-config-btn">
              {configLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
